import React, { useState, useRef, useEffect } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';

const Tooltip = ({
  content,
  children,
  position = 'top',
  delay = 200,
  disabled = false,
  wrapperClassName = ""
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const tooltipRef = useRef(null);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    setTooltipPosition(position);
  }, [position]);

  useEffect(() => {
    if (isVisible && tooltipRef.current && wrapperRef.current) {
      const tooltip = tooltipRef.current;
      const wrapper = wrapperRef.current;
      const rect = tooltip.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();

      // Auto-adjust position if tooltip goes out of viewport
      let adjustedPosition = position;

      if (position === 'top' && wrapperRect.top < rect.height + 10) {
        adjustedPosition = 'bottom';
      } else if (
        (position === 'bottom' || position === 'bottom-end') &&
        window.innerHeight - wrapperRect.bottom < rect.height + 10
      ) {
        adjustedPosition = position === 'bottom-end' ? 'top-end' : 'top';
      } else if (position === 'bottom-end' && wrapperRect.right - rect.width < 8) {
        adjustedPosition = 'bottom-start';
      } else if (position === 'left' && wrapperRect.left < rect.width + 10) {
        adjustedPosition = 'right';
      } else if (position === 'right' && window.innerWidth - wrapperRect.right < rect.width + 10) {
        adjustedPosition = 'left';
      }

      setTooltipPosition(adjustedPosition);
    }
  }, [isVisible, position]);

  const handleMouseEnter = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!content || disabled) {
    return <>{children}</>;
  }

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    'bottom-end': 'top-full right-0 mt-2',
    'bottom-start': 'top-full left-0 mt-2',
    'top-end': 'bottom-full right-0 mb-2',
    'top-start': 'bottom-full left-0 mb-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 transform -translate-x-1/2 border-t-gray-900 dark:border-t-gray-700 border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-900 dark:border-b-gray-700 border-l-transparent border-r-transparent border-t-transparent',
    'bottom-end':
      'bottom-full right-3 border-b-gray-900 dark:border-b-gray-700 border-l-transparent border-r-transparent border-t-transparent',
    'bottom-start':
      'bottom-full left-3 border-b-gray-900 dark:border-b-gray-700 border-l-transparent border-r-transparent border-t-transparent',
    'top-end':
      'top-full right-3 border-t-gray-900 dark:border-t-gray-700 border-l-transparent border-r-transparent border-b-transparent',
    'top-start':
      'top-full left-3 border-t-gray-900 dark:border-t-gray-700 border-l-transparent border-r-transparent border-b-transparent',
    left: 'left-full top-1/2 transform -translate-y-1/2 border-l-gray-900 dark:border-l-gray-700 border-t-transparent border-r-transparent border-b-transparent',
    right: 'right-full top-1/2 transform -translate-y-1/2 border-r-gray-900 dark:border-r-gray-700 border-t-transparent border-l-transparent border-b-transparent',
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative inline-block ${wrapperClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className={`absolute z-50 max-w-[min(280px,calc(100vw-2rem))] px-3 py-2 text-xs font-medium rounded-lg shadow-lg pointer-events-none whitespace-normal text-left ${positionClasses[tooltipPosition] || positionClasses.bottom} ${isDarkMode
              ? 'bg-gray-700 text-gray-100'
              : 'bg-gray-900 text-white'
            }`}
        >
          {content}
          <div
            className={`absolute w-0 h-0 border-4 ${arrowClasses[tooltipPosition] || arrowClasses.bottom}`}
          />
        </div>
      )}
    </div>
  );
};

export default Tooltip;
